import React, { useCallback, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSelected, useFocused } from 'slate-react';
import { Settings, X, Upload, Image, Palette, Type } from 'lucide-react';

// Import background images
import mountain from '../../../../../assets/images/mountain.jpeg';
import page1chart from '../../../../../assets/images/page1chart.png';

// Background image options
const BACKGROUND_OPTIONS = {
  mountain: { label: 'Mountain', src: mountain },
  page1chart: { label: 'Word Cloud', src: page1chart },
  gradient: { label: 'Gradient', src: null },
  solid: { label: 'Solid Color', src: null },
};

// Font size options for title (using pixel values for inline styles)
const TITLE_SIZE_OPTIONS = [
  { value: '2xl', label: 'Small', fontSize: '1.5rem' },    // 24px
  { value: '3xl', label: 'Medium', fontSize: '1.875rem' }, // 30px
  { value: '4xl', label: 'Large', fontSize: '2.25rem' },   // 36px
  { value: '5xl', label: 'XL', fontSize: '3rem' },         // 48px
  { value: '6xl', label: 'XXL', fontSize: '3.75rem' },     // 60px
];

// Font size options for subtitle
const SUBTITLE_SIZE_OPTIONS = [
  { value: 'sm', label: 'Small', fontSize: '0.875rem' },  // 14px
  { value: 'base', label: 'Medium', fontSize: '1rem' },   // 16px
  { value: 'lg', label: 'Large', fontSize: '1.125rem' },  // 18px
  { value: 'xl', label: 'XL', fontSize: '1.25rem' },      // 20px
];

// Common text color presets
const TEXT_COLOR_PRESETS = [
  '#ffffff', '#f8fafc', '#e2e8f0', '#fef08a', '#fde047', 
  '#fbbf24', '#f97316', '#ef4444', '#ec4899', '#a855f7',
  '#3b82f6', '#06b6d4', '#10b981', '#22c55e', '#000000'
];

// Settings Panel Portal - renders outside the hero element to avoid clipping
function SettingsPortal({ children, buttonRef }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right + window.scrollX - 380, // Panel width
      });
    }
  }, [buttonRef]);

  return ReactDOM.createPortal(
    <div 
      style={{
        position: 'absolute',
        top: position.top,
        left: Math.max(16, position.left), // Keep within viewport
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

function HeroElement({ attributes, children, element, onUpdate, onDelete }) {
  const selected = useSelected();
  const focused = useFocused();
  const [showSettings, setShowSettings] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [activeTab, setActiveTab] = useState('text'); // 'text', 'gallery', 'upload', 'colors'
  const [isUploading, setIsUploading] = useState(false);
  const settingsButtonRef = useRef(null);
  const fileInputRef = useRef(null);

  const {
    title = 'Enter Title',
    subtitle = 'Enter subtitle',
    backgroundImage = 'mountain',
    customBackgroundUrl = '', // For uploaded/custom images
    overlayColor = '#bb133e',
    overlayOpacity = 0.75,
    // Text styling options
    titleColor = '#ffffff',
    titleSize = '4xl',
    titleBold = true,
    subtitleColor = '#ffffff',
    subtitleSize = 'lg',
    subtitleOpacity = 0.9,
  } = element;

  // Close settings when clicking outside
  useEffect(() => {
    if (showSettings) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.hero-settings-panel') && 
            !e.target.closest('.hero-settings-button')) {
          setShowSettings(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettings]);

  // Check if using custom image
  const isCustomImage = backgroundImage === 'custom' && customBackgroundUrl;

  // Get background style
  const getBackgroundStyle = () => {
    // Custom uploaded image
    if (isCustomImage) {
      return {
        backgroundImage: `url(${customBackgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    
    // Preset image
    const bg = BACKGROUND_OPTIONS[backgroundImage];
    if (bg?.src) {
      return {
        backgroundImage: `url(${bg.src})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    
    // Gradient
    if (backgroundImage === 'gradient') {
      return {
        background: `linear-gradient(135deg, ${overlayColor} 0%, #1a1a1a 100%)`,
      };
    }
    
    // Solid color
    return {
      backgroundColor: overlayColor,
    };
  };

  // Check if we should show overlay controls
  const hasImageBackground = isCustomImage || BACKGROUND_OPTIONS[backgroundImage]?.src;

  // Get title font size (inline style)
  const getTitleFontSize = () => {
    const option = TITLE_SIZE_OPTIONS.find(o => o.value === titleSize);
    return option ? option.fontSize : '2.25rem';
  };

  // Get subtitle font size (inline style)
  const getSubtitleFontSize = () => {
    const option = SUBTITLE_SIZE_OPTIONS.find(o => o.value === subtitleSize);
    return option ? option.fontSize : '1.125rem';
  };

  const handleTitleChange = useCallback((value) => {
    if (onUpdate) {
      onUpdate(element.id, { title: value });
    }
  }, [element.id, onUpdate]);

  const handleSubtitleChange = useCallback((value) => {
    if (onUpdate) {
      onUpdate(element.id, { subtitle: value });
    }
  }, [element.id, onUpdate]);

  const handleBackgroundChange = useCallback((bg) => {
    if (onUpdate) {
      onUpdate(element.id, { backgroundImage: bg, customBackgroundUrl: '' });
    }
  }, [element.id, onUpdate]);

  const handleOverlayColorChange = useCallback((color) => {
    if (onUpdate) {
      onUpdate(element.id, { overlayColor: color });
    }
  }, [element.id, onUpdate]);

  const handleOverlayOpacityChange = useCallback((opacity) => {
    if (onUpdate) {
      onUpdate(element.id, { overlayOpacity: parseFloat(opacity) });
    }
  }, [element.id, onUpdate]);

  // Text styling handlers
  const handleTitleColorChange = useCallback((color) => {
    if (onUpdate) {
      onUpdate(element.id, { titleColor: color });
    }
  }, [element.id, onUpdate]);

  const handleTitleSizeChange = useCallback((size) => {
    if (onUpdate) {
      onUpdate(element.id, { titleSize: size });
    }
  }, [element.id, onUpdate]);

  const handleTitleBoldChange = useCallback((bold) => {
    if (onUpdate) {
      onUpdate(element.id, { titleBold: bold });
    }
  }, [element.id, onUpdate]);

  const handleSubtitleColorChange = useCallback((color) => {
    if (onUpdate) {
      onUpdate(element.id, { subtitleColor: color });
    }
  }, [element.id, onUpdate]);

  const handleSubtitleSizeChange = useCallback((size) => {
    if (onUpdate) {
      onUpdate(element.id, { subtitleSize: size });
    }
  }, [element.id, onUpdate]);

  const handleSubtitleOpacityChange = useCallback((opacity) => {
    if (onUpdate) {
      onUpdate(element.id, { subtitleOpacity: parseFloat(opacity) });
    }
  }, [element.id, onUpdate]);

  // Handle file upload
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB.');
      return;
    }

    setIsUploading(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result;
      if (dataUrl && onUpdate) {
        onUpdate(element.id, { 
          backgroundImage: 'custom',
          customBackgroundUrl: dataUrl 
        });
      }
      setIsUploading(false);
    };
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  }, [element.id, onUpdate]);

  // Trigger file input click
  const handleUploadClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  }, []);

  // Proper event handlers for Slate
  const handleSettingsClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(!showSettings);
  }, [showSettings]);

  const handleDeleteClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(element.id);
    }
  }, [element.id, onDelete]);

  const handleCloseSettings = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(false);
  }, []);

  const handleTitleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditingTitle(true);
  }, []);

  const handleSubtitleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditingSubtitle(true);
  }, []);

  // Common title styles
  const titleStyle = {
    color: titleColor,
    fontSize: getTitleFontSize(),
    fontWeight: titleBold ? 'bold' : 'normal',
  };

  // Common subtitle styles
  const subtitleStyle = {
    color: subtitleColor,
    fontSize: getSubtitleFontSize(),
    opacity: subtitleOpacity,
  };

  return (
    <div 
      {...attributes}
      contentEditable={false}
      className={`hero-element relative rounded-xl overflow-hidden my-4 transition-all ${
        selected && focused ? 'ring-4 ring-blue-500 shadow-xl' : 'hover:ring-2 hover:ring-gray-300'
      }`}
    >
      {children}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Background */}
      <div 
        className="hero-background relative"
        style={{
          ...getBackgroundStyle(),
          minHeight: '300px',
        }}
      >
        {/* Overlay - shown for image backgrounds */}
        {hasImageBackground && (
          <div 
            className="absolute inset-0"
            style={{ 
              backgroundColor: overlayColor,
              opacity: overlayOpacity,
            }}
          />
        )}

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center text-center p-12 min-h-[300px]">
          {/* Title - Editable */}
          {isEditingTitle ? (
            <input
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              onBlur={() => setIsEditingTitle(false)}
              onKeyDown={(e) => e.key === 'Enter' && setIsEditingTitle(false)}
              autoFocus
              className="bg-transparent border-b-2 text-center placeholder-white/50 outline-none w-full max-w-2xl"
              style={{ 
                ...titleStyle,
                borderColor: titleColor + '80',
              }}
              placeholder="Enter title..."
            />
          ) : (
            <h1 
              className="mb-4 cursor-text hover:opacity-80 transition-opacity"
              style={titleStyle}
              onMouseDown={handleTitleClick}
            >
              {title}
            </h1>
          )}

          {/* Subtitle - Editable */}
          {isEditingSubtitle ? (
            <textarea
              value={subtitle}
              onChange={(e) => handleSubtitleChange(e.target.value)}
              onBlur={() => setIsEditingSubtitle(false)}
              autoFocus
              className="bg-transparent border-b-2 text-center placeholder-white/50 outline-none w-full max-w-3xl resize-none"
              style={{ 
                ...subtitleStyle,
                borderColor: subtitleColor + '50',
              }}
              placeholder="Enter subtitle..."
              rows={2}
            />
          ) : (
            <p 
              className="max-w-3xl cursor-text hover:opacity-80 transition-opacity"
              style={subtitleStyle}
              onMouseDown={handleSubtitleClick}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Controls - visible when selected or on hover */}
      <div 
        className={`absolute top-4 right-4 flex items-center gap-2 z-20 transition-opacity ${
          selected ? 'opacity-100' : 'opacity-0 hover:opacity-100'
        }`}
        contentEditable={false}
      >
        <button
          ref={settingsButtonRef}
          type="button"
          onMouseDown={handleSettingsClick}
          className="hero-settings-button p-2 bg-white/90 rounded-lg shadow-md hover:bg-white cursor-pointer"
          title="Settings"
        >
          <Settings size={16} />
        </button>
        {onDelete && (
          <button
            type="button"
            onMouseDown={handleDeleteClick}
            className="p-2 bg-white/90 rounded-lg shadow-md hover:bg-red-100 cursor-pointer"
            title="Delete"
          >
            <X size={16} className="text-red-500" />
          </button>
        )}
      </div>

      {/* Settings Panel - rendered via portal to avoid clipping */}
      {showSettings && (
        <SettingsPortal buttonRef={settingsButtonRef}>
          <div 
            className="hero-settings-panel bg-white rounded-lg shadow-2xl border p-4 min-w-[380px] max-h-[550px] overflow-y-auto"
            contentEditable={false}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">Hero Settings</h4>
              <button
                type="button"
                onMouseDown={handleCloseSettings}
                className="p-1 hover:bg-gray-100 rounded cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b mb-4 overflow-x-auto">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setActiveTab('text'); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'text' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Type size={16} />
                Text
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setActiveTab('gallery'); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'gallery' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Image size={16} />
                Gallery
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setActiveTab('upload'); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'upload' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Upload size={16} />
                Upload
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setActiveTab('colors'); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'colors' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Palette size={16} />
                Overlay
              </button>
            </div>

            {/* Text Tab */}
            {activeTab === 'text' && (
              <div className="space-y-5">
                {/* Title Settings */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h5 className="text-sm font-semibold text-gray-700 mb-3">Title</h5>
                  
                  {/* Title Color */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={titleColor}
                        onChange={(e) => handleTitleColorChange(e.target.value)}
                        className="w-8 h-8 border rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={titleColor}
                        onChange={(e) => handleTitleColorChange(e.target.value)}
                        className="flex-1 border rounded px-2 py-1 text-xs"
                        placeholder="#ffffff"
                      />
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {TEXT_COLOR_PRESETS.slice(0, 10).map((color) => {
                        // Light colors need a visible border
                        const isLightColor = ['#ffffff', '#f8fafc', '#e2e8f0', '#fef08a', '#fde047'].includes(color);
                        return (
                          <button
                            key={color}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleTitleColorChange(color);
                            }}
                            className={`w-6 h-6 rounded cursor-pointer transition-transform ${
                              titleColor === color ? 'ring-2 ring-blue-500 scale-110' : 'hover:scale-105'
                            }`}
                            style={{ 
                              backgroundColor: color, 
                              border: isLightColor ? '1px solid #d1d5db' : '1px solid transparent' 
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* Title Size */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Size</label>
                    <div className="flex gap-1">
                      {TITLE_SIZE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleTitleSizeChange(option.value);
                          }}
                          className={`flex-1 px-2 py-1.5 text-xs border rounded cursor-pointer transition-colors ${
                            titleSize === option.value 
                              ? 'bg-blue-100 border-blue-500 text-blue-700' 
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Title Bold */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={titleBold}
                        onChange={(e) => handleTitleBoldChange(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-xs font-medium text-gray-600">Bold</span>
                    </label>
                  </div>
                </div>

                {/* Subtitle Settings */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h5 className="text-sm font-semibold text-gray-700 mb-3">Subtitle</h5>
                  
                  {/* Subtitle Color */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={subtitleColor}
                        onChange={(e) => handleSubtitleColorChange(e.target.value)}
                        className="w-8 h-8 border rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={subtitleColor}
                        onChange={(e) => handleSubtitleColorChange(e.target.value)}
                        className="flex-1 border rounded px-2 py-1 text-xs"
                        placeholder="#ffffff"
                      />
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {TEXT_COLOR_PRESETS.slice(0, 10).map((color) => {
                        // Light colors need a visible border
                        const isLightColor = ['#ffffff', '#f8fafc', '#e2e8f0', '#fef08a', '#fde047'].includes(color);
                        return (
                          <button
                            key={color}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSubtitleColorChange(color);
                            }}
                            className={`w-6 h-6 rounded cursor-pointer transition-transform ${
                              subtitleColor === color ? 'ring-2 ring-blue-500 scale-110' : 'hover:scale-105'
                            }`}
                            style={{ 
                              backgroundColor: color, 
                              border: isLightColor ? '1px solid #d1d5db' : '1px solid transparent' 
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* Subtitle Size */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Size</label>
                    <div className="flex gap-1">
                      {SUBTITLE_SIZE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSubtitleSizeChange(option.value);
                          }}
                          className={`flex-1 px-2 py-1.5 text-xs border rounded cursor-pointer transition-colors ${
                            subtitleSize === option.value 
                              ? 'bg-blue-100 border-blue-500 text-blue-700' 
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Subtitle Opacity */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Opacity: {Math.round(subtitleOpacity * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0.3"
                      max="1"
                      step="0.05"
                      value={subtitleOpacity}
                      onChange={(e) => handleSubtitleOpacityChange(e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">Select a preset background</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(BACKGROUND_OPTIONS).filter(([key]) => BACKGROUND_OPTIONS[key].src).map(([key, { label, src: imgSrc }]) => (
                    <button
                      key={key}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleBackgroundChange(key);
                      }}
                      className={`p-2 border rounded-lg hover:bg-blue-50 text-left cursor-pointer transition-colors ${
                        backgroundImage === key && !isCustomImage ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <img 
                        src={imgSrc} 
                        alt={label} 
                        className="w-full h-16 object-cover rounded mb-1"
                      />
                      <span className="text-xs text-gray-600">{label}</span>
                    </button>
                  ))}
                </div>
                
                {/* Gradient and Solid options */}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBackgroundChange('gradient');
                    }}
                    className={`flex-1 p-3 border rounded-lg text-center cursor-pointer transition-colors ${
                      backgroundImage === 'gradient' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div 
                      className="w-full h-8 rounded mb-1"
                      style={{ background: `linear-gradient(135deg, ${overlayColor} 0%, #1a1a1a 100%)` }}
                    />
                    <span className="text-xs text-gray-600">Gradient</span>
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBackgroundChange('solid');
                    }}
                    className={`flex-1 p-3 border rounded-lg text-center cursor-pointer transition-colors ${
                      backgroundImage === 'solid' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div 
                      className="w-full h-8 rounded mb-1"
                      style={{ backgroundColor: overlayColor }}
                    />
                    <span className="text-xs text-gray-600">Solid Color</span>
                  </button>
                </div>
              </div>
            )}

            {/* Upload Tab */}
            {activeTab === 'upload' && (
              <div className="mb-4">
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                  onMouseDown={handleUploadClick}
                >
                  {isUploading ? (
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                      <p className="text-gray-600">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 font-medium mb-1">Click to upload background</p>
                      <p className="text-gray-400 text-sm">PNG, JPG, GIF up to 5MB</p>
                    </>
                  )}
                </div>
                
                {/* Show current custom image */}
                {isCustomImage && (
                  <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Current custom background:</p>
                    <img 
                      src={customBackgroundUrl} 
                      alt="Custom background" 
                      className="max-h-24 mx-auto rounded"
                    />
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleBackgroundChange('mountain'); // Reset to default
                      }}
                      className="mt-2 text-xs text-red-500 hover:text-red-700 cursor-pointer"
                    >
                      Remove custom image
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Colors/Overlay Tab */}
            {activeTab === 'colors' && (
              <div className="mb-4">
                {/* Overlay Color */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {hasImageBackground ? 'Overlay Color' : 'Background Color'}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={overlayColor}
                      onChange={(e) => handleOverlayColorChange(e.target.value)}
                      className="w-12 h-12 border rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={overlayColor}
                      onChange={(e) => handleOverlayColorChange(e.target.value)}
                      className="flex-1 border rounded-md px-3 py-2 text-sm"
                      placeholder="#000000"
                    />
                  </div>
                </div>

                {/* Quick color presets */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Presets</label>
                  <div className="flex flex-wrap gap-2">
                    {['#bb133e', '#1e40af', '#047857', '#7c3aed', '#dc2626', '#0891b2', '#4f46e5', '#059669', '#d97706', '#1f2937'].map((color) => (
                      <button
                        key={color}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleOverlayColorChange(color);
                        }}
                        className={`w-8 h-8 rounded cursor-pointer border-2 transition-all ${
                          overlayColor === color ? 'border-blue-500 scale-110' : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                {/* Overlay Opacity - only for image backgrounds */}
                {hasImageBackground && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Overlay Opacity: {Math.round(overlayOpacity * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={overlayOpacity}
                      onChange={(e) => handleOverlayOpacityChange(e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>Transparent</span>
                      <span>Opaque</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </SettingsPortal>
      )}
    </div>
  );
}

export default HeroElement;
